import { message } from 'antd'
import { SimpleCustomerDto } from 'enrollment-platform/api/persons/dto/simple-customer.dto'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  fetchActiveCustomers,
  getPresignedUploadUrl,
  processHrisReport,
  uploadHrisReport,
  uploadSplitHrisReport,
} from '../../../api/hris-api-client'
import { isQueuedUpload } from '../../../../CensusManagement/utils/census-upload-job'

export interface UseUploadCensusFileProps {
  // isQueued: the upload was handed to a background job
  onOk: (isQueued: boolean) => void
  onCancel: () => void
  isSplitEntityEnabled?: boolean
}

interface UploadState {
  clients: SimpleCustomerDto[]
  selectedFiles: File[]
  selectedClient?: SimpleCustomerDto
  loading: boolean
  selectedCensusVersion: number
}

const MAX_SPLIT_CLIENT_FILES = 5

const initialState: UploadState = {
  clients: [],
  selectedFiles: [],
  selectedClient: undefined,
  loading: false,
  selectedCensusVersion: 1,
}

const useUploadCensusFile = ({
  onOk,
  onCancel,
  isSplitEntityEnabled = true,
}: UseUploadCensusFileProps) => {
  const [state, setState] = useState<UploadState>(initialState)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const handleCompanyChange = useCallback(
    (value: string) => {
      setState((prev) => {
        const selectedClient = prev.clients.find((c) => c.id === value)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }

        return {
          ...prev,
          selectedClient,
          selectedFiles: [],
        }
      })
    },
    [isSplitEntityEnabled]
  )

  const handleCensusVersionChange = useCallback((value: number) => {
    setState((prev) => ({
      ...prev,
      selectedCensusVersion: value,
    }))
  }, [])

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files ?? [])
      const isSplitClient = state.selectedClient?.isSplitClient ?? false

      if (isSplitClient && files.length > MAX_SPLIT_CLIENT_FILES) {
        message.error(
          `Split clients can upload a maximum of ${MAX_SPLIT_CLIENT_FILES} files.`
        )

        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }

        setState((prev) => ({ ...prev, selectedFiles: [] }))
        return
      }

      if (files.length > 0) {
        setState((prev) => ({ ...prev, selectedFiles: files }))
      }
    },
    [state.selectedClient]
  )

  const resetFileInput = useCallback(() => {
    if (fileInputRef.current) fileInputRef.current.value = ''
    setState((prev) => ({ ...prev, selectedFiles: [] }))
  }, [])

  const resetModalSelections = useCallback(() => {
    if (fileInputRef.current) fileInputRef.current.value = ''
    setState((prev) => ({
      ...prev,
      selectedFiles: [],
      selectedClient: undefined,
    }))
  }, [])

  const onCloseModal = useCallback(() => {
    onCancel()
    resetModalSelections()
  }, [onCancel, resetModalSelections])

  const openFileDialog = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleUpload = useCallback(async () => {
    const { selectedFiles, selectedClient, selectedCensusVersion } = state
    if (selectedFiles.length === 0 || !selectedClient) {
      message.error('Please select a file and a client before uploading.')
      return
    }

    if (selectedClient.isSplitClient && !isSplitEntityEnabled) {
      message.error('This feature is not supported.')
      return
    }

    if (
      selectedClient.isSplitClient &&
      selectedFiles.length > MAX_SPLIT_CLIENT_FILES
    ) {
      message.error(
        `Split clients can upload a maximum of ${MAX_SPLIT_CLIENT_FILES} files.`
      )
      return
    }

    setState((prev) => ({ ...prev, loading: true }))

    try {
      if (selectedClient.isSplitClient) {
        const { isQueued } = await uploadSplitHrisReport({
          clientPublicKey: selectedClient.id,
          censusVersion: selectedCensusVersion,
          files: selectedFiles,
        })

        resetFileInput()
        onOk(isQueued)
        return
      }

      const results = await Promise.all(
        selectedFiles.map(async (selectedFile) => {
          const { name: fileName } = selectedFile
          const { presignedUploadUrl, hrisCensusReportId } =
            await getPresignedUploadUrl({
              clientId: selectedClient.id,
              fileName,
              censusVersion: selectedCensusVersion,
            })

          if (!presignedUploadUrl) {
            throw new Error('Unable to retrieve presigned upload url')
          }

          const uploadSuccess = await uploadHrisReport(
            presignedUploadUrl,
            selectedFile
          )

          if (!uploadSuccess) {
            throw new Error('Unable to upload file using presigned upload url')
          }

          return processHrisReport({
            fileName,
            hrisCensusReportId,
            clientPublicKey: selectedClient.id,
            censusVersion: selectedCensusVersion,
          })
        })
      )

      resetFileInput()
      onOk(results.some(isQueuedUpload))
    } catch (error) {
      message.error(`Failed to upload or process file. ${error}`)
    } finally {
      setState((prev) => ({ ...prev, loading: false }))
    }
  }, [state, isSplitEntityEnabled, onOk, resetFileInput])

  const transformedClient = useMemo(
    () =>
      (state?.clients ?? []).map(({ name, id }) => ({
        companyName: name,
        id: id,
      })),
    [state?.clients]
  )

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const customers = await fetchActiveCustomers()
        setState((prev) => ({ ...prev, clients: customers }))
      } catch (error) {
        console.error(error)
      }
    }
    fetchClients()
  }, [])

  return {
    ...state,
    selectedFile: state.selectedFiles[0] ?? null,
    transformedClient,
    fileInputRef,
    handleUpload,
    handleCompanyChange,
    handleCensusVersionChange,
    handleFileSelect,
    openFileDialog,
    onCloseModal,
  }
}

export default useUploadCensusFile
